import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useToast } from "@/hooks/useToast";

// Types for Job Card Audit System
export interface AuditEntry {
  id: string;
  job_card_id: string;
  table_name: string;
  record_id: string;
  action: 'insert' | 'update' | 'delete' | 'status_change' | 'custom';
  field_name?: string;
  old_value?: any;
  new_value?: any;
  change_description?: string;
  user_id: string;
  user_name?: string;
  timestamp: string;
  category: 'operation' | 'quantity' | 'quality' | 'material' | 'attachment' | 'defect' | 'operator' | 'system';
  severity: 'info' | 'warning' | 'error' | 'critical';
  metadata?: Record<string, any>;
}

export interface HistoryFilter {
  categories?: string[];
  actions?: string[];
  dateFrom?: string;
  dateTo?: string;
  userId?: string;
  tableName?: string;
  severity?: string[];
}

export interface AuditLogRequest {
  jobCardId: string;
  tableName: string;
  recordId: string;
  action: AuditEntry['action'];
  fieldName?: string;
  oldValue?: any;
  newValue?: any;
  changeDescription?: string;
  userId: string;
  category: AuditEntry['category'];
  severity?: AuditEntry['severity'];
  metadata?: Record<string, any>;
}

type UUID = string;

/**
 * Hook for managing Job Card Audit and History
 * Provides comprehensive change tracking, event logging, and historical analysis
 */
export const useJobCardAudit = () => {
  const [loading, setLoading] = useState(false);
  const [auditEntries, setAuditEntries] = useState<AuditEntry[]>([]);
  const { toast } = useToast();

  /**
   * Fetch audit history for a job card with optional filtering
   */
  const fetchAuditHistory = useCallback(async (
    jobCardId: UUID, 
    filter?: HistoryFilter
  ): Promise<AuditEntry[]> => {
    try {
      setLoading(true);
      
      // First, fetch the activities without joining users
      let query = supabase
        .from('job_card_activities')
        .select(`
          *
        `)
        .eq('job_card_id', jobCardId)
        .order('created_at', { ascending: false });
      
      // Apply filters
      if (filter) {
        if (filter.dateFrom) {
          query = query.gte('created_at', filter.dateFrom);
        }
        if (filter.dateTo) {
          query = query.lte('created_at', filter.dateTo);
        }
        if (filter.userId) {
          query = query.eq('user_id', filter.userId);
        }
        if (filter.tableName) {
          query = query.eq('table_name', filter.tableName);
        }
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      // Extract unique user IDs from the activities
      const userIds = [...new Set((data || []).map((item: any) => item.user_id).filter(Boolean))];
      
      // Fetch user data from public.users table
      let userData: any[] = [];
      if (userIds.length > 0) {
        const { data: users, error: userError } = await supabase
          .from('users')
          .select('id, email, full_name')
          .in('id', userIds);
        
        if (userError) {
          console.error('Error fetching user data:', userError);
        } else {
          userData = users || [];
        }
      }
      
      // Create a map of user data for quick lookup
      const userMap = new Map(userData.map(user => [user.id, user]));
      
      // Map to AuditEntry format and categorize
      const entries: AuditEntry[] = (data || []).map((item: any) => {
        const user = userMap.get(item.user_id);
        const category = categorizeAuditEntry(item.table_name, item.action, item.field_name);
        const severity = determineSeverity(item.action, category, item.new_value);
        
        // Extract user name from full_name if available
        let userName = 'Unknown User';
        if (user?.full_name) {
          userName = user.full_name;
        } else if (user?.email) {
          userName = user.email;
        }
        
        return {
          id: item.id,
          job_card_id: jobCardId,
          table_name: item.table_name,
          record_id: item.record_id,
          action: item.action,
          field_name: item.field_name,
          old_value: parseAuditValue(item.old_value),
          new_value: parseAuditValue(item.new_value),
          change_description: generateChangeDescription(item),
          user_id: item.user_id,
          user_name: userName,
          timestamp: item.timestamp,
          category,
          severity,
          metadata: extractMetadata(item),
        };
      });
      
      // Apply additional filters that require processed data
      let filteredEntries = entries;
      if (filter?.categories?.length) {
        filteredEntries = filteredEntries.filter(entry => 
          filter.categories!.includes(entry.category)
        );
      }
      if (filter?.actions?.length) {
        filteredEntries = filteredEntries.filter(entry => 
          filter.actions!.includes(entry.action)
        );
      }
      if (filter?.severity?.length) {
        filteredEntries = filteredEntries.filter(entry => 
          filter.severity!.includes(entry.severity)
        );
      }
      
      setAuditEntries(filteredEntries);
      return filteredEntries;
    } catch (error: any) {
      toast({
        title: 'Error fetching audit history',
        description: error.message,
        variant: 'destructive',
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, [toast]);

  /**
   * Create a new audit log entry
   */
  const createAuditEntry = useCallback(async (request: AuditLogRequest): Promise<AuditEntry | null> => {
    try {
      const auditData = {
        table_name: request.tableName,
        record_id: request.recordId,
        action: request.action,
        field_name: request.fieldName,
        old_value: serializeAuditValue(request.oldValue),
        new_value: serializeAuditValue(request.newValue),
        user_id: request.userId,
        timestamp: new Date().toISOString(),
        metadata: request.metadata
      };
      
      const { data, error } = await supabase
        .from('job_card_activities')
        .insert(auditData)
        .select('*')
        .single();
      
      if (error) throw error;
      
      const auditEntry: AuditEntry = {
        id: data.id,
        job_card_id: request.jobCardId,
        table_name: request.tableName,
        record_id: request.recordId,
        action: request.action,
        field_name: request.fieldName,
        old_value: request.oldValue,
        new_value: request.newValue,
        change_description: request.changeDescription || generateChangeDescription(data),
        user_id: request.userId,
        user_name: undefined,
        timestamp: data.timestamp,
        category: request.category,
        severity: request.severity || 'info',
        metadata: request.metadata,
      };
      
      return auditEntry;
    } catch (error: any) {
      toast({
        title: 'Error creating audit entry',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    }
  }, [toast]);

  /**
   * Get audit summary statistics for a job card
   */
  const getAuditSummary = useCallback((entries: AuditEntry[]) => {
    const summary = {
      total_changes: entries.length,
      by_category: {} as Record<string, number>,
      by_action: {} as Record<string, number>,
      by_severity: {} as Record<string, number>,
      by_user: {} as Record<string, number>,
      recent_activity: entries.slice(0, 10),
      critical_events: entries.filter(e => e.severity === 'critical'),
      date_range: {
        first_activity: entries.length > 0 ? entries[entries.length - 1]?.timestamp : null,
        last_activity: entries.length > 0 ? entries[0]?.timestamp : null
      }
    };
    
    entries.forEach(entry => {
      // Count by category
      summary.by_category[entry.category] = 
        (summary.by_category[entry.category] || 0) + 1;
      
      // Count by action
      summary.by_action[entry.action] = 
        (summary.by_action[entry.action] || 0) + 1;
      
      // Count by severity
      summary.by_severity[entry.severity] = 
        (summary.by_severity[entry.severity] || 0) + 1;
      
      // Count by user
      const userName = entry.user_name || 'Unknown User';
      summary.by_user[userName] = 
        (summary.by_user[userName] || 0) + 1;
    });
    
    return summary;
  }, []);

  /**
   * Export audit history to various formats
   */
  const exportAuditHistory = useCallback(async (
    jobCardId: UUID,
    format: 'csv' | 'json' | 'pdf',
    filter?: HistoryFilter
  ): Promise<string | null> => {
    try {
      const entries = await fetchAuditHistory(jobCardId, filter);
      
      switch (format) {
        case 'csv':
          return exportToCSV(entries);
        case 'json':
          return JSON.stringify(entries, null, 2);
        case 'pdf':
          // For PDF export, we would typically use a library like jsPDF
          // For now, return a structured text format
          return exportToText(entries);
        default:
          throw new Error('Unsupported export format');
      }
    } catch (error: any) {
      toast({
        title: 'Error exporting audit history',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    }
  }, [fetchAuditHistory, toast]);

  /**
   * Compare two points in time for a job card
   */
  const compareJobCardStates = useCallback(async (
    jobCardId: UUID,
    fromTimestamp: string,
    toTimestamp: string
  ): Promise<any> => {
    try {
      const entries = await fetchAuditHistory(jobCardId, {
        dateFrom: fromTimestamp,
        dateTo: toTimestamp
      });
      
      const changes = {
        period: {
          from: fromTimestamp,
          to: toTimestamp
        },
        total_changes: entries.length,
        field_changes: {} as Record<string, any>,
        status_changes: entries.filter(e => e.field_name === 'status'),
        critical_events: entries.filter(e => e.severity === 'critical'),
        user_activity: {} as Record<string, number>
      };
      
      // Analyze field-level changes
      entries.forEach(entry => {
        if (entry.field_name) {
          if (!changes.field_changes[entry.field_name]) {
            changes.field_changes[entry.field_name] = {
              changes: 0,
              first_value: entry.old_value,
              last_value: entry.new_value
            };
          }
          changes.field_changes[entry.field_name].changes++;
          changes.field_changes[entry.field_name].last_value = entry.new_value;
        }
        
        // Count user activity
        const userName = entry.user_name || 'Unknown User';
        changes.user_activity[userName] = (changes.user_activity[userName] || 0) + 1;
      });
      
      return changes;
    } catch (error: any) {
      toast({
        title: 'Error comparing job card states',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    }
  }, [fetchAuditHistory, toast]);

  /**
   * Search audit history with text-based queries
   */
  const searchAuditHistory = useCallback(async (
    jobCardId: UUID,
    searchQuery: string
  ): Promise<AuditEntry[]> => {
    try {
      const allEntries = await fetchAuditHistory(jobCardId);
      
      const searchLower = searchQuery.toLowerCase();
      const matchingEntries = allEntries.filter(entry => {
        return (
          entry.change_description?.toLowerCase().includes(searchLower) ||
          entry.field_name?.toLowerCase().includes(searchLower) ||
          entry.user_name?.toLowerCase().includes(searchLower) ||
          entry.action.toLowerCase().includes(searchLower) ||
          entry.category.toLowerCase().includes(searchLower) ||
          JSON.stringify(entry.new_value)?.toLowerCase().includes(searchLower) ||
          JSON.stringify(entry.old_value)?.toLowerCase().includes(searchLower)
        );
      });
      
      return matchingEntries;
    } catch (error: any) {
      toast({
        title: 'Error searching audit history',
        description: error.message,
        variant: 'destructive',
      });
      return [];
    }
  }, [fetchAuditHistory, toast]);

  return {
    loading,
    auditEntries,
    fetchAuditHistory,
    createAuditEntry,
    getAuditSummary,
    exportAuditHistory,
    compareJobCardStates,
    searchAuditHistory
  };
};

// Helper functions

function categorizeAuditEntry(tableName: string, action: string, fieldName?: string): AuditEntry['category'] {
  if (tableName && tableName.includes('operator') || tableName && tableName.includes('time_log')) {
    return 'operator';
  }
  if (tableName && tableName.includes('material') || tableName && tableName.includes('inventory')) {
    return 'material';
  }
  if (tableName && tableName.includes('qc') || tableName && tableName.includes('quality')) {
    return 'quality';
  }
  if (tableName && tableName.includes('attachment') || tableName && tableName.includes('document')) {
    return 'attachment';
  }
  if (tableName && tableName.includes('defect') || tableName && tableName.includes('ncr')) {
    return 'defect';
  }
  if (fieldName === 'quantity' || fieldName && fieldName.includes('qty')) {
    return 'quantity';
  }
  if (tableName === 'job_cards' || action && action.includes('status')) {
    return 'operation';
  }
  return 'system';
}

function determineSeverity(action: string, category: string, newValue: any): AuditEntry['severity'] {
  if (action === 'delete' || category === 'defect') {
    return 'error';
  }
  if (action === 'status_change' && typeof newValue === 'string' && newValue) {
    if (newValue.includes('Hold') || newValue.includes('Failed')) {
      return 'critical';
    }
    if (newValue.includes('Completed') || newValue.includes('Started')) {
      return 'warning';
    }
  }
  if (category === 'quality' && typeof newValue === 'string' && newValue && newValue.includes('Fail')) {
    return 'critical';
  }
  return 'info';
}

function generateChangeDescription(auditItem: any): string {
  const { action, field_name, table_name, old_value, new_value } = auditItem;
  
  if (action === 'insert') {
    return `Created new ${table_name} record`;
  }
  if (action === 'delete') {
    return `Deleted ${table_name} record`;
  }
  if (action === 'update' && field_name) {
    return `Changed ${field_name} from "${old_value}" to "${new_value}"`;
  }
  if (action === 'status_change') {
    return `Status changed from "${old_value}" to "${new_value}"`;
  }
  
  return `${action} performed on ${table_name}`;
}

function parseAuditValue(value: any): any {
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }
  return value;
}

function serializeAuditValue(value: any): string {
  if (typeof value === 'object' && value !== null) {
    return JSON.stringify(value);
  }
  return String(value);
}

function extractMetadata(auditItem: any): Record<string, any> {
  const metadata: Record<string, any> = {};
  
  if (auditItem.new_value && typeof auditItem.new_value === 'object') {
    // Extract useful metadata from complex objects
    if (auditItem.new_value.job_card_id) {
      metadata.job_card_id = auditItem.new_value.job_card_id;
    }
    if (auditItem.new_value.work_order_id) {
      metadata.work_order_id = auditItem.new_value.work_order_id;
    }
  }
  
  return metadata;
}

function exportToCSV(entries: AuditEntry[]): string {
  const headers = [
    'Timestamp', 'User', 'Action', 'Category', 'Severity', 
    'Table', 'Field', 'Old Value', 'New Value', 'Description'
  ];
  
  const rows = entries.map(entry => [
    entry.timestamp,
    entry.user_name || entry.user_id,
    entry.action,
    entry.category,
    entry.severity,
    entry.table_name,
    entry.field_name || '',
    JSON.stringify(entry.old_value) || '',
    JSON.stringify(entry.new_value) || '',
    entry.change_description || ''
  ]);
  
  return [headers, ...rows]
    .map(row => row.map(cell => `"${cell}"`).join(','))
    .join('\n');
}

function exportToText(entries: AuditEntry[]): string {
  let text = 'Job Card Audit History Report\n';
  text += '='.repeat(50) + '\n\n';
  
  entries.forEach(entry => {
    text += `[${entry.timestamp}] ${entry.severity.toUpperCase()}\n`;
    text += `User: ${entry.user_name || entry.user_id}\n`;
    text += `Action: ${entry.action} (${entry.category})\n`;
    text += `Description: ${entry.change_description}\n`;
    if (entry.field_name) {
      text += `Field: ${entry.field_name}\n`;
      text += `Changed from: ${JSON.stringify(entry.old_value)}\n`;
      text += `Changed to: ${JSON.stringify(entry.new_value)}\n`;
    }
    text += '-'.repeat(30) + '\n';
  });
  
  return text;
}
